/**
 * Globaler Toast (2026-06-05 spät) — iOS-stilisierte Pille oben mittig.
 * Ersetzt die heutigen Flash-Texte + roten Pflichtfeld-Box. Kein Modal/Pop-up
 * (User-Anforderung: Pop-up reißt Flow auf).
 *
 * Verwendung:
 *
 *   import { useToast } from "../components/Toast";
 *   const toast = useToast();
 *   toast.success("Gespeichert");
 *   toast.warn("Vorsicht: ...");
 *   toast.error("Pflichtfelder fehlen", { missing: ["Vendor", "Belegdatum"] });
 *
 * Persistenz:
 * - success/warn fade nach 4s automatisch raus
 * - error bleibt sichtbar bis Klick (oder bis explicit dismiss)
 *
 * Mounten:
 * - <ToastProvider> einmal um den App-Root in main.tsx wrappen
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

type ToastKind = "success" | "warn" | "error";

type ToastItem = {
  id: number;
  kind: ToastKind;
  message: string;
  missing?: string[]; // optionale Liste (z.B. fehlende Pflichtfelder)
};

type ToastApi = {
  success: (message: string) => void;
  warn: (message: string) => void;
  error: (message: string, opts?: { missing?: string[] }) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  let nextId = 0;

  const push = useCallback((kind: ToastKind, message: string, missing?: string[]) => {
    const id = ++nextId;
    setItems((prev) => [...prev, { id, kind, message, missing }]);
    // success + warn fade automatisch raus; error bleibt sichtbar bis Klick.
    if (kind !== "error") {
      setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const api = useMemo<ToastApi>(() => ({
    success: (message) => push("success", message),
    warn: (message) => push("warn", message),
    error: (message, opts) => push("error", message, opts?.missing),
    dismiss: (id) => setItems((prev) => prev.filter((t) => t.id !== id)),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer items={items} onDismiss={api.dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() ohne <ToastProvider> benutzt");
  return ctx;
}

function ToastContainer({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  if (items.length === 0) return null;
  return (
    <div
      className="fixed top-safe inset-x-0 z-50 pointer-events-none flex flex-col items-center gap-2 px-4 pt-3"
      role="region"
      aria-label="Benachrichtigungen"
    >
      {items.map((t) => (
        <ToastPill key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastPill({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  // Mount-Animation: leichte Slide-In + Fade-In.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  const colors = (() => {
    switch (item.kind) {
      case "success": return { bg: "bg-ios-green/95", text: "text-white", border: "border-ios-green/0" }; // erledigt = grün (User 18.10.)
      case "warn":    return { bg: "bg-ios-orange/95", text: "text-white", border: "border-ios-orange/0" };
      case "error":   return { bg: "bg-white", text: "text-ios-ink", border: "border-ios-red/40" };
    }
  })();

  const Icon = item.kind === "success" ? CheckCircleIcon : ExclamationTriangleIcon;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto max-w-md w-full ${colors.bg} ${colors.border} border rounded-iosLg shadow-card px-3.5 py-2.5 backdrop-blur transition-all duration-200 ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${item.kind === "error" ? "text-ios-red" : colors.text}`} />
        <div className={`flex-1 min-w-0 ${colors.text}`}>
          <div className="text-[14px] font-medium">{item.message}</div>
          {item.missing && item.missing.length > 0 && (
            <ul className="mt-1.5 list-disc list-inside text-[12px] space-y-0.5">
              {item.missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className={`shrink-0 -mr-1 -mt-1 p-1 rounded-ios hover:bg-black/10 ${colors.text}`}
          aria-label="Schließen"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
